import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { IkonsMark } from "./IkonsMark";
import { CONTACT } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", club: "", role: "", email: "", message: "" };

const inputClass =
  "w-full border border-ink-600/70 bg-ink-800 px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-200 focus:border-kit";

export const Contact = () => {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm(EMPTY);
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="scroll-mt-20 py-20 sm:py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow={t.contact.eyebrow} title={t.contact.title} testId="contact-heading" />
        </Reveal>

        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <Reveal>
            <div className="flex h-full flex-col justify-between gap-10">
              <div>
                <IkonsMark className="h-14" />
                <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-300" data-testid="contact-lead">
                  {t.contact.lead}
                </p>
              </div>
              <div className="border border-ink-700/60 bg-ink-900 p-6">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
                  {t.contact.directLabel}
                </p>
                <p
                  className="mt-4 font-display text-2xl font-bold uppercase leading-tight text-white"
                  data-testid="contact-agent-name"
                >
                  {CONTACT.agentName}
                </p>
                <p className="mt-1 text-sm text-zinc-400" data-testid="contact-agent-role">
                  {CONTACT.agentRole[lang]} · {CONTACT.agency}
                </p>
                <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <a
                    data-testid="contact-email-button"
                    href={`mailto:${CONTACT.email}`}
                    className="inline-flex items-center gap-2 border border-kit/50 bg-kit/10 px-5 py-3 text-sm font-bold text-kit transition-colors duration-200 hover:bg-kit hover:text-ink-950"
                  >
                    <Mail size={16} /> {CONTACT.email}
                  </a>
                  <a
                    data-testid="contact-phone-button"
                    href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                    className="inline-flex items-center gap-2 border border-ink-600 px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:border-kit hover:text-kit"
                  >
                    <Phone size={16} /> {CONTACT.phone}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              className="border border-ink-700/60 bg-ink-900 p-6 sm:p-8"
              data-testid="contact-form"
            >
              <p className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-kit">
                {t.contact.formTitle}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-400">
                    {t.contact.name} *
                  </label>
                  <input
                    id="cf-name"
                    data-testid="contact-form-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.contact.namePh}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-club" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-400">
                    {t.contact.club}
                  </label>
                  <input
                    id="cf-club"
                    data-testid="contact-form-club-input"
                    value={form.club}
                    onChange={set("club")}
                    placeholder={t.contact.clubPh}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-role" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-400">
                    {t.contact.role}
                  </label>
                  <input
                    id="cf-role"
                    data-testid="contact-form-role-input"
                    value={form.role}
                    onChange={set("role")}
                    placeholder={t.contact.rolePh}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-400">
                    {t.contact.email} *
                  </label>
                  <input
                    id="cf-email"
                    data-testid="contact-form-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder={t.contact.emailPh}
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-400">
                    {t.contact.message} *
                  </label>
                  <textarea
                    id="cf-message"
                    data-testid="contact-form-message-input"
                    required
                    minLength={5}
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.contact.messagePh}
                    className={`${inputClass} resize-y`}
                  />
                </div>
              </div>
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="mt-6 inline-flex items-center gap-2 bg-kit px-8 py-3 text-sm font-bold uppercase tracking-[0.1em] text-ink-950 transition-colors duration-200 hover:bg-kit-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send size={15} /> {sending ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
